import React from 'react'
import { mark } from '../data/record'

// One screening block. Every entry is laid out differently, but always on the
// same grid and always flush with the block above it. The earlier version
// varied the offsets too, which made the page read as floating fragments.
//
// `layout` picks the shape, `band` the background, `split` the ratio:
//   image-right / image-left  a two-column split
//   full-bleed                the photograph full width, text over it
//   masonry                   the premiere
//
// Bands run blue, off-white, grey and white and never repeat twice in a row.

const Rule = () => <span className='event-rule' aria-hidden='true' />

const Meta = ({ event, dark }) => (
  <div className={`event-meta ${dark ? 'event-meta--dark' : ''}`}>
    <h3 className='event-title'>
      {event.url
        ? <a href={event.url} target='_blank' rel='noopener noreferrer'>{event.title}</a>
        : event.title}
    </h3>
    <Rule />
    <p className='event-date'>
      {event.date}{event.place ? ` · ${event.place}` : ''}
    </p>
    {event.note && <p className='event-note'>{event.note}</p>}

    {event.links && event.links.length > 0 && (
      <p className='event-links'>
        {event.links.map((link, i) => (
          <React.Fragment key={link.href}>
            {i > 0 && <span className='event-links-sep'>·</span>}
            <a href={link.href} target='_blank' rel='noopener noreferrer'>{link.label}</a>
          </React.Fragment>
        ))}
      </p>
    )}

    {event.reel && (
      <p className='event-reel'>
        <a className={`link-button ${dark ? 'link-button--light' : ''}`}
           href={event.reel} target='_blank' rel='noopener noreferrer'>Watch the reel</a>
      </p>
    )}
  </div>
)

// Photographs are shown at their own proportions. Nothing is cropped to fill a
// shape. `small: true` on an image renders it at a fraction of the column.
const Figure = ({ images }) => (
  <div className='event-figure'>
    {images.map((image) => (
      <img key={image.src} src={image.src} alt={image.alt} loading='lazy'
           className={image.small ? 'is-small' : ''} />
    ))}
  </div>
)

const Event = ({ event }) => {
  const band = event.band || 'off-white'
  const dark = band === 'blue'
  const cls = `event event--${event.layout} event--band-${band}` +
              (event.split ? ` event--split-${event.split}` : '')

  if (event.layout === 'masonry') {
    const featured = event.featured || []
    const large = event.images.filter((_, i) => featured.includes(i))
    const rest = event.images.filter((_, i) => !featured.includes(i))
    return (
      <section className={cls} data-tone={dark ? 'dark' : 'light'}>
        <div className='content-container'><Meta event={event} /></div>
        {large.length > 0 && (
          <ul className='masonry-feature'>
            {large.map((im) => <li key={im.src}><img src={im.src} alt={im.alt} loading='lazy' /></li>)}
          </ul>
        )}
        <ul className='masonry'>
          {rest.map((im) => <li key={im.src}><img src={im.src} alt={im.alt} loading='lazy' /></li>)}
        </ul>
      </section>
    )
  }

  if (event.layout === 'type') {
    return (
      <section className={cls} data-tone={dark ? 'dark' : 'light'}>
        <div className='content-container'><Meta event={event} dark={dark} /></div>
      </section>
    )
  }

  if (event.layout === 'full-bleed') {
    return (
      <section className={cls} data-tone='dark'>
        <div className='event-bleed'>
          <img src={event.images[0].src} alt={event.images[0].alt} loading='lazy' />
          <div className='event-bleed-veil' aria-hidden='true' />
          <div className='event-bleed-text'>
            <div className='content-container'><Meta event={event} dark /></div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className={cls} data-tone={dark ? 'dark' : 'light'}>
      <div className='event-text'><Meta event={event} dark={dark} /></div>
      <Figure images={event.images} />
    </section>
  )
}

export const Mark = ({ home }) => (
  <div className={`section-mark ${home ? 'section-mark--home' : ''}`}>
    <img src={mark} alt='' aria-hidden='true' />
  </div>
)

const EventList = ({ events }) => (
  <div className='event-list'>
    {events.map((e) => <Event key={e.title + e.date} event={e} />)}
  </div>
)

export default EventList
